import api from 'api';
import {
  EarningsComponentType,
  NonPayableBenefitsComponentType,
  SalaryComponent,
} from 'components/SalaryComponents/types';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { useQuery } from 'react-query';
import { convertToStructureComponent } from '../utils';

export const useSalaryStructureComponents = () => {
  // Fetch earning components
  const {
    data: earningsData,
    isLoading: isEarningsLoading,
    error: earningsError,
  } = useQuery(['salaryComponents', 'earnings'], () =>
    api.salaryComponents.queries.listSalaryComponents<SalaryComponent>({
      category: 'EARNINGS_CATEGORY',
      status: 'COMPONENT_STATUS_ACTIVE',
    }),
  );

  // Process and categorize components
  const allEarningsData = (earningsData?.payrollListSalaryComponents?.data?.salaryComponents ||
    []) as EarningsComponentType[];

  // Separate regular earnings from non-payable benefits
  const earningComponents = allEarningsData.filter(
    (component) => component.settings?.earningDetails?.payType !== 'non_payable_benefit',
  );

  const earningComponentsWithoutAdhoc = earningComponents.filter(
    (component) => component.settings?.earningDetails?.payType !== 'adhoc',
  );

  const nonPayableBenefitComponents = allEarningsData.filter(
    (component) => component.settings?.earningDetails?.payType === 'non_payable_benefit',
  ) as NonPayableBenefitsComponentType[];

  // Categorize earnings into fixed and variable
  const fixedEarningComponents = earningComponents.filter(
    (component) => component.settings?.earningDetails.payType === 'fixed',
  );

  const variableEarningComponents = earningComponents.filter(
    (component) => component.settings?.earningDetails?.payType === 'variable',
  );

  // Convert to structure components format
  const fixedEarningStructureComponents: SalaryStructureComponent[] = fixedEarningComponents.map(
    (component) => convertToStructureComponent(component),
  );

  const variableEarningStructureComponents: SalaryStructureComponent[] =
    variableEarningComponents.map((component) => convertToStructureComponent(component));

  const nonPayableBenefitStructureComponents: SalaryStructureComponent[] =
    nonPayableBenefitComponents.map((component) => convertToStructureComponent(component));

  const allComponents = [...earningComponents, ...nonPayableBenefitComponents];
  const allNonAdhocComponents = [...earningComponentsWithoutAdhoc, ...nonPayableBenefitComponents];
  const isLoading = isEarningsLoading;
  const error = earningsError;

  return {
    allComponents,
    allNonAdhocComponents,
    earningComponents,
    nonPayableBenefitComponents,
    fixedEarningComponents,
    variableEarningComponents,
    fixedEarningStructureComponents,
    variableEarningStructureComponents,
    nonPayableBenefitStructureComponents,
    isLoading,
    error,
  };
};
